/**
 * One sales document, whichever of the four kinds it is.
 *
 * This route sits outside `SalesShell` (see `SalesSection.tsx`), so nothing
 * above it draws a heading - the page has to name itself, which is what the
 * `PageHead` here is for. It arrived from the pre-redesign look as a bordered
 * box with a shadcn button bar on top and a grey `<table>` in the middle; what
 * replaces it is the CRM's own surface, the same status pill the lists use, and
 * a line grid that matches the editor column for column.
 *
 * The mutations live in `useDocActions`; everything below is layout.
 */

import { useState, type CSSProperties, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { showVatOn } from "@wa/core/sales-vat";
import {
  Building2,
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  FileText,
  Loader2,
  Receipt,
  Trash2,
  User,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  formatCents,
  type CreditNote, type Estimate, type Invoice, type LineItem, type Proposal,
} from "../../../lib/sales";
import { Btn, Card, CardHead, EmptyState, Modal, PageHead, daysFromNow, fmtDate } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { DOC_CONFIG, SHARE_TYPE, type DocKind } from "./config";
import { SendConfirmModal } from "../../mail/SendConfirmModal";
import { sendPreviewFor } from "./sendPreview";
import { DocTotals, StatusBadge } from "./shared";
import { useInvoicingVat } from "../../useInvoicingVat";
import { DocCommentsCard } from "./DocCommentsCard";
import { DocDetailActions } from "./DocDetailActions";
import { DocLines } from "./DocLines";
import { DocPayments } from "./DocPayments";
import { PortalDecisionCard } from "./PortalDecisionCard";
import { PublicLinkCard } from "./PublicLinkCard";
import { DocViewsCard } from "./DocViewsCard";
import { RecurringCard, type RecurringHost } from "./RecurringCard";
import { useDocActions } from "./useDocActions";

/** One fact from the strip under the head. */
function Fact({ icon, label, value, sub }: { icon: ReactNode; label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="crm-q">
      <span className="crm-q-tile">{icon}</span>
      <span className="min-w-0">
        <span className="crm-q-lab block">{label}</span>
        <span className="crm-q-val crm-trunc block">{value}</span>
        {sub ? <span className="crm-prow-s block">{sub}</span> : null}
      </span>
    </div>
  );
}


export function DocDetail({ kind, user }: { kind: DocKind; user: StaffUser }) {
  const t = useT();
  const { vatEnabled } = useInvoicingVat();
  const cfg = DOC_CONFIG[kind];
  const { id } = useParams<{ id: string }>();
  const [confirmDel, setConfirmDel] = useState(false);
  const canManage = userCanPlatform(user, "manage:crm.sales");
  // Starting a project from a won estimate is a PROJECTS right, not a sales one.
  // The section door only proves the operator may work with the document.
  const canProjects = userCanPlatform(user, "manage:crm.projects");
  const canMail = userCanPlatform(user, "manage:crm.email");
  const {
    doc, loading, busy, pdfBusy, reload, changeStatus, send, sendPreview, cancelSend, confirmSend,
    downloadPdf, convert, createProject, markPaid, remove,
  } = useDocActions(kind, id);

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="crm-card">
        <EmptyState
          icon={FileText}
          title={t("sales.notFound")}
          action={
            <Link to={`/sales/${kind}`} className="crm-btn">
              <ChevronLeft className="size-4" />
              {t("sales.back")}
            </Link>
          }
        />
      </div>
    );
  }

  const inv = doc as Invoice;
  const lines = (doc.lineItems ?? []) as LineItem[];
  // The same answer the line grid below reaches, worked out once here so the
  // table and the totals under it cannot disagree about whether this document
  // charges VAT.
  const showVat = showVatOn({ vatEnabled, taxCents: doc.taxCents, lines });
  const docTitle = (doc as Proposal).title || null;
  const reason = kind === "credit-notes" ? (doc as CreditNote).reason : null;
  // Carried by the two kinds a client can answer, and only when the CLIENT
  // answered - in the portal, or on the public link.
  const portalDecision =
    kind === "estimates" ? (doc as Estimate).portalDecision
      : kind === "proposals" ? (doc as Proposal).portalDecision
        : null;
  // The old first-visit stamp, which only invoices carry. It is passed to the
  // views card as a FALLBACK: documents opened before the visit log existed
  // have nothing else, and dropping the column from the screen would lose that
  // history. Absent means "no proof", never "not seen" - see `DocViewsCard`.
  const portalViewedAt = kind === "invoices" ? inv.viewedAt : null;
  // The public link and the thread under it exist for three of the four kinds.
  const shareType = SHARE_TYPE[kind];
  const dateIso = cfg.hasIssueDate ? inv.issueDate : doc.createdAt;
  const deadlineIso = cfg.hasDueDate ? inv.dueDate : cfg.hasValidUntil ? (doc as Proposal).validUntil : null;
  /* Client, date, total, plus the deadline when the kind has one. */
  const factCount = deadlineIso ? 4 : 3;

  return (
    // One open menu at a time across the head and every payment row.
    <PopHost>
      <div>
        <Link to={`/sales/${kind}`} className="crm-btn crm-btn-sm mb-4">
          <ChevronLeft className="size-4" />
          {t("sales.back")}
        </Link>

        <PageHead
          // The number is the document's name; the status sits beside it rather
          // than in a column of its own, because on a single record the state is
          // part of what you are looking at, not a field of it.
          title={
            <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {doc.number}
              <StatusBadge status={doc.status} />
            </span>
          }
          sub={docTitle || reason || doc.client?.name}
          actions={
            <DocDetailActions
              doc={doc}
              cfg={cfg}
              kind={kind}
              canManage={canManage}
              canProjects={canProjects}
              canMail={canMail}
              busy={busy}
              pdfBusy={pdfBusy}
              onDownloadPdf={downloadPdf}
              onSend={send}
              onConvert={convert}
              onCreateProject={createProject}
              onMarkPaid={markPaid}
              onStatus={changeStatus}
              onDelete={() => setConfirmDel(true)}
            />
          }
        />

        <Card className="crm-quick" style={{ "--crm-quick-cols": factCount } as CSSProperties}>
          <Fact
            icon={doc.client?.kind === "person" ? <User className="size-4" /> : <Building2 className="size-4" />}
            label={t("sales.client")}
            value={
              doc.client ? (
                <Link to={`/clients/${doc.client.id}`} className="hover:underline">
                  {doc.client.name}
                </Link>
              ) : (
                "-"
              )
            }
          />
          <Fact
            icon={<CalendarDays className="size-4" />}
            label={cfg.hasIssueDate ? t("sales.issueDate") : t("sales.col.date")}
            value={<span className="crm-tnum">{fmtDate(dateIso)}</span>}
          />
          {deadlineIso ? (
            <Fact
              icon={<CalendarClock className="size-4" />}
              label={cfg.hasDueDate ? t("sales.dueDate") : t("sales.validUntil")}
              value={<span className="crm-tnum">{fmtDate(deadlineIso)}</span>}
              sub={dueNote(t, deadlineIso, cfg.hasPayments && inv.paidCents >= inv.totalCents)}
            />
          ) : null}
          <Fact
            icon={<Receipt className="size-4" />}
            label={t("sales.totals.total")}
            value={<span className="crm-tnum">{formatCents(doc.totalCents, doc.currency)}</span>}
          />
        </Card>

        <Card>
          <CardHead title={t("sales.lines")} />
          <DocLines lines={lines} qtyMode={doc.qtyMode ?? "qty"} />
          <div className="flex justify-end px-[26px] pb-7 pt-3">
            <DocTotals
              t={t}
              showVat={showVat}
              subtotalCents={doc.subtotalCents}
              taxCents={doc.taxCents}
              totalCents={doc.totalCents}
              paidCents={cfg.hasPayments ? inv.paidCents : undefined}
            />
          </div>
        </Card>

        {doc.notes ? (
          <Card className="mt-4 px-[26px] py-[22px]">
            <div className="crm-card-t mb-2.5">{t("sales.notes")}</div>
            <p className="crm-mut whitespace-pre-wrap text-[13px] leading-relaxed">{doc.notes}</p>
          </Card>
        ) : null}

        {portalDecision ? <PortalDecisionCard decision={portalDecision} /> : null}

        {/* The address a client opens without an account, when they opened it,
            and the conversation that happens there. Under the document itself,
            because all three are about the document rather than about the
            desk's own workflow. */}
        {shareType && doc.id ? (
          <>
            <PublicLinkCard type={shareType} id={doc.id} canManage={canManage} />
            <DocViewsCard type={shareType} id={doc.id} fallbackViewedAt={portalViewedAt} />
            <DocCommentsCard type={shareType} id={doc.id} canManage={canManage} />
          </>
        ) : null}

        {cfg.hasPayments ? <DocPayments invoice={inv} canManage={canManage} onChange={reload} /> : null}

        {/* Below the payments, because the money that arrived is the more urgent
            fact about an invoice than the money it will bill again next month.
            The card draws itself in both states - see its header for why an
            invoice with NO schedule still shows one. */}
        {cfg.hasRecurrence ? (
          <RecurringCard invoice={inv as unknown as RecurringHost} canManage={canManage} onChange={reload} />
        ) : null}

        {sendPreview ? (
          <SendConfirmModal
            preview={sendPreviewFor(sendPreview, t)}
            busy={busy}
            onCancel={cancelSend}
            onConfirm={confirmSend}
          />
        ) : null}

        {confirmDel ? (
          <Modal
            title={t("sales.delete")}
            label={t("sales.delete")}
            onClose={() => setConfirmDel(false)}
            footer={
              <>
                <Btn onClick={() => setConfirmDel(false)}>{t("common.cancel")}</Btn>
                <Btn
                  tone="danger"
                  onClick={() => {
                    setConfirmDel(false);
                    remove();
                  }}
                >
                  <Trash2 className="size-4" />
                  {t("sales.delete")}
                </Btn>
              </>
            }
          >
            <p className="crm-mut text-[13.25px] leading-relaxed">{t("sales.deleteConfirm")}</p>
          </Modal>
        ) : null}
      </div>
    </PopHost>
  );
}

/**
 * How long is left, under the deadline date. A date on its own makes the reader
 * do the arithmetic, and the answer is the whole reason the date is on screen.
 *
 * A plain function rather than a component so that "nothing to say" really is
 * nothing: an element that renders null still leaves its wrapper behind.
 */
function dueNote(t: ReturnType<typeof useT>, iso: string, settled: boolean): ReactNode {
  if (settled) return null;
  const days = daysFromNow(iso);
  if (days === null) return null;
  if (days === 0) return t("sales.dueToday");
  if (days < 0) return <span className="text-red-600">{t("sales.dueOverdue", { n: -days })}</span>;
  return t("sales.dueIn", { n: days });
}
